import moment from "moment";
import React, { useContext, useMemo, useState } from "react";
import { useEffect } from "react";
import GetCurrency from "../../../../../helpers/services";
import {
	CurrencyWithAmount,
	addAttributesToLinks,
	checkIfFreeTicketAndReferral,
	errorToaster
} from "../../../../../helpers/utils";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import ReadMore from "../../../../../components/ReadMoreText";
import { useMediaQuery } from "react-responsive";
import DiscountTicketContent from "../../../../../components/Modal/DiscountTicketContent";
import { discountContentByCondition, hasGroupDiscountOffer, isMatchingUrlForHarcodeText } from "../../../../../helpers/common";
import DOMPurify from "dompurify";
import { CHECKOUT_MAX_TICKETS, RESTRICTED_EVENT_ID } from "../../../config/constants";
import WidgetContext from "../../../../../contexts/WidgetContext";

const Ticket = ({
	query,
	eventDetails,
	isWidget,
	donationEnabled,
	setDonationEnabled,
	teamRegType,
	groupDiscounts,
	...props
}) => {
	const { totalActiveRegs } = useContext(WidgetContext)
	const [ticket, setTicket] = useState({});
	const [donationAmount, setDonationAmount] = useState(
		props.ticketInfo.ticket_price || props.ticketInfo.minimum_price
	);
	const [errors, setErrors] = useState({});
	const [donationAmountEnabled, setDonationAmountEnabled] = useState(false);
	const [automaticDiscount, setAutomaticDiscount] = useState();
	const [showDiscountContent, setShowDiscountContent] = useState(false);
	const [isTicketAboveLimit, setIsTicketAboveLimit] = useState(false);
	const discount_conditon = [1, 2];
	const showVenue = ticket.venue && eventDetails.venue === ticket.venue ? false : true;
	const showAdditionalVenue =
		eventDetails.additional_venue_details_rich_text ===
			ticket.additional_venue_details_rich_text
			? false
			: true;

	const totalTicketPurchased = useMemo(() => props.selectedTickets.reduce((sum, ticket) => {
		return sum + ticket.totalSelected;
	}, 0), [props.selectedTickets]);

	const isMatchedEvent = isMatchingUrlForHarcodeText(eventDetails?.event_url);
	const handleMediaQueryChange = matches => {
		smallerScreen = matches;
		// matches will be true or false based on the value for the media query
	};
	let smallerScreen = useMediaQuery({ maxWidth: 768 }, undefined, handleMediaQueryChange);

	const styles = {
		fontFamily: "Prompt",
		fontWeight: 400,
		fontSize: "14px",
		margin: "0px"
	};

	const restrictedEventId = eventDetails?.event_id === RESTRICTED_EVENT_ID.ID

	useEffect(() => {
		// whenever prop.ticketInfo loads or updates, setState
		setTicket(props.ticketInfo);
		if (props.ticketInfo.automatic_discount_rules !== null)
			setAutomaticDiscount(props.ticketInfo.automatic_discount_rules);
	}, [props.ticketInfo]);

	useEffect(() => {
		if (props.ticketInfo.donationAmountEnabled) {
			donationAmountValidate(Number(props.ticketInfo.minimum_price));
			if (props.ticketInfo.minimum_price < 0) {
				setDonationAmount(null);
				addDonationTicket(0);
			}
			addDonationTicket(props.ticketInfo.minimum_price, props.ticketInfo);
			setDonationAmount(props.ticketInfo.minimum_price);
		}
	}, [props.ticketInfo]);

	useEffect(() => {
		if (props.ticketInfo.ticket_type === 3 && props.ticketInfo?.donation_price) {
			if (
				donationEnabled ||
				props.ticketInfo.donation_price >= props.ticketInfo.minimum_price
			) {
				setDonationEnabled(false);
				setDonationAmountEnabled(true);
			}
		}
	}, [donationEnabled, props.ticketInfo]);

	const addTicket = (totalSelected, isJoinWaitlist = false) => {
		const selectedTicket = ticket;
		const isFreeRefferal = checkIfFreeTicketAndReferral(query, ticket);
		const remainingAccessCode = selectedTicket.remaining - 1;
		if (totalSelected > remainingAccessCode) {
			errorToaster("Cannot select anymore tickets with this access code.", "ticket-id");
			return;
		}
		if (selectedTicket.discount) {
			const isCouponCodeOver = props.couponCodeCheck(
				"add",
				totalSelected === 0 ? selectedTicket.min_tickets : 1
			);
			if (isCouponCodeOver) {
				errorToaster("Cannot select anymore tickets with this coupon code.", "ticket-id");
			} else {
				if (totalSelected === 0 && !isJoinWaitlist && !isFreeRefferal) {
					selectedTicket.totalSelected += selectedTicket.team_registration ?
						eventDetails.min_team_members :
						selectedTicket.min_tickets;
				}
				else selectedTicket.totalSelected += 1; //Else in case of waitlist or free refferal registration always add 1 ticket only
				setTicket(selectedTicket);
				props.updateSelectedTicket(selectedTicket); //Pass Ticket Details
			}
		} else {
			if (totalSelected === 0 && !isJoinWaitlist && !isFreeRefferal) {
				selectedTicket.totalSelected += selectedTicket.team_registration ?
					eventDetails.min_team_members :
					selectedTicket.min_tickets;
			}
			else selectedTicket.totalSelected += 1; //Else in case of waitlist or free refferal registration always add 1 ticket only
			setTicket(selectedTicket);
			props.updateSelectedTicket(selectedTicket); //Pass Ticket Details
		}
	};

	const addDonationTicket = (amount, selectedTicket = ticket) => {
		amount = Number(amount);

		let pass = true;
		if (amount === null || amount === 0 || amount === "") pass = false;
		if (selectedTicket.minimum_price) {
			if (amount < selectedTicket.minimum_price) pass = false;
		}
		if (selectedTicket.maximum_price) {
			if (amount > selectedTicket.maximum_price) pass = false;
		}
		if (!selectedTicket.minimum_price && !selectedTicket.maximum_price && amount <= 0)
			pass = false;

		if (pass) {
			selectedTicket.totalSelected = 1;
			selectedTicket.ticket_price = amount;
			selectedTicket.donation_price = amount;
			setTicket(selectedTicket);
			props.updateSelectedTicket(selectedTicket); //Pass Ticket Details
		} else {
			if (selectedTicket.totalSelected !== 0) {
				selectedTicket.ticket_price = 0;
				selectedTicket.totalSelected = 0;
				selectedTicket.donation_price = 0;
				setTicket(selectedTicket);
				props.removeTicket(selectedTicket);
			}
		}
	};

	const donationAmountValidate = amount => {
		amount = Number(amount);
		const selectedTicket = ticket;
		const validationErrors = {};
		if (selectedTicket.minimum_price) {
			if (amount < selectedTicket.minimum_price)
				validationErrors.donationAmount = `The price has to be greater or equal to ${selectedTicket.minimum_price}`;
		}
		if (selectedTicket.maximum_price) {
			if (amount > selectedTicket.maximum_price)
				validationErrors.donationAmount = `The price has to be lesser or equal to ${selectedTicket.maximum_price}`;
		}
		if (!selectedTicket.minimum_price && !selectedTicket.maximum_price && amount <= 0)
			validationErrors.donationAmount = "Please enter a valid price, greater than 0";
		setErrors(validationErrors);
	};

	const removeTicket = totalSelected => {
		const selectedTicket = ticket;
		if (selectedTicket.discount) {
			props.couponCodeCheck(
				"remove",
				totalSelected === selectedTicket.min_tickets ? selectedTicket.min_tickets : 1
			);
		}
		if (selectedTicket.team_registration) {
			totalSelected === eventDetails.min_team_members
				? (selectedTicket.totalSelected -= eventDetails.min_team_members)
				: (selectedTicket.totalSelected -= 1);
		} else {
			totalSelected === selectedTicket.min_tickets
				? (selectedTicket.totalSelected -= selectedTicket.min_tickets)
				: (selectedTicket.totalSelected -= 1);
		}
		if (selectedTicket.totalSelected === 0) {
			props.removeTicket(selectedTicket);
			setTicket(selectedTicket);
		} else {
			setTicket(selectedTicket);
			props.updateSelectedTicket(selectedTicket); //Pass Ticket Details
		}
	};

	const isTeamTicketAboveLimit = () => {
		let totalRegistrations =
			sessionStorage.getItem("team_attendee_count") && teamRegType === 2
				? parseInt(sessionStorage.getItem("team_attendee_count"))
				: 0; //if join team, add the registered attendee count else 0
		for (const ticket of props.selectedTickets) {
			totalRegistrations += ticket.totalSelected;
		}
		if (totalRegistrations >= eventDetails.max_team_members)
			return true; //if create or join team, user cant select above max team tickets
		else return false;
	};

	useEffect(() => {
		//using array.reduce method
		const totalRegistrations = props.selectedTickets.reduce(
			(acc, ticket) => acc + ticket.totalSelected,
			0
		);
		if (totalRegistrations >= CHECKOUT_MAX_TICKETS) {
			setIsTicketAboveLimit(true);
		} else {
			setIsTicketAboveLimit(false);
		}
	}, [props.selectedTickets]);

	const disableAddButton = (ticket, otherCurrency) => {
		// when total registration exceeds the event registration limit based on their plan
		if (
			eventDetails.max_regs_count &&
			totalTicketPurchased + totalActiveRegs >= eventDetails.max_regs_count
		) {
			return true;
		}
		if (isTicketAboveLimit) return true;
		//for team based tickets
		if (teamRegType > 0 && isTeamTicketAboveLimit()) {
			return true;
		}
		if (checkIfFreeTicketAndReferral(query, ticket) || (otherCurrency && !ticket.is_free))
			return true;
		else return false;
	};
	const disabledAddButtonContent = (ticket) => {
		// when total registration exceeds the event registration limit based on their plan
		if (
			eventDetails.max_regs_count &&
			(totalTicketPurchased + totalActiveRegs >= eventDetails.max_regs_count)
		) {
			return "Your ticket selection exceeds the remaining available limit. Please reduce the number of tickets to select more.";
		}
		//for team based tickets
		if (isTicketAboveLimit) {
			return `You have selected maximum allowed tickets in a single registration flow`;
		}
		if (teamRegType > 0 && isTeamTicketAboveLimit()) {
			return `You have selected maximum allowed tickets in a single registration flow`;
		}
		if (checkIfFreeTicketAndReferral(query, ticket)) {
			return "Please remove other selected tickets to make a free refferal registration";
		} else {
			return "Tickets in different currencies cannot be bought together";
		}
	};

	const disablePlusButtonContent = () => { // for + button
		// when total registration exceeds the event registration limit based on their plan
		if (
			eventDetails.max_regs_count &&
			totalTicketPurchased + totalActiveRegs >= eventDetails.max_regs_count
		) {
			return "Your ticket selection exceeds the remaining available limit. Please reduce the number of tickets to select more.";
		}
		if (isTicketAboveLimit) {
			return `You have selected maximum allowed tickets in a single registration flow`;
		}

		return "You have selected maximum allowed tickets in a single registration flow";
	}

	const disableButton = ticket => {
		// when total registration exceeds the event registration limit based on their plan
		if (
			eventDetails.max_regs_count &&
			(totalTicketPurchased + totalActiveRegs >= eventDetails.max_regs_count)
		) {
			return true;
		}
		if (isTicketAboveLimit) return true;
		if (teamRegType > 0) {
			//for team based tickets
			return isTeamTicketAboveLimit();
		} else if (ticket.totalSelected >= ticket.remaining_count) {
			return true;
		} else if (ticket.totalSelected >= ticket.max_tickets) {
			return true;
		} else false;
	};

	const buttonType = ticket => {
		var todaysDate = moment().valueOf(); // convert utc to local
		const ticketStartDate = moment.utc(`${ticket.start_timestamp}`).local().valueOf();
		const ticketEndDate = moment.utc(`${ticket.end_timestamp}`).local().valueOf();
		const eventEndDate = moment
			.utc(`${eventDetails.end_date} ${eventDetails.end_time}`)
			.local()
			.valueOf();

		const otherCurrency = props.selectedTickets.find(
			obj => !obj.is_free && obj.currency_name !== ticket.currency_name
		);

		switch (true) {
			case todaysDate > eventEndDate:
				return (
					<button className="ticket-button sold-out-btn" disabled>
						Expired
					</button>
				);
			case todaysDate > ticketEndDate ||
				(ticket.sold_out && !ticket.waitlist_enabled) ||
				(ticket.remaining_count <= 0 && !ticket.waitlist_enabled):
				return (
					<button className="ticket-button sold-out-btn" disabled>
						Sold Out
					</button>
				);
			case ticketStartDate > todaysDate:
				return (
					<button className="ticket-button sold-out-btn" disabled>
						Coming Soon
					</button>
				);
			case ticket.approval_enabled:
				return (
					<>
						{_.isEmpty(props.selectedTickets) ? (
							<button
								className="ticket-button"
								onClick={() => {
									addTicket(ticket.totalSelected, true);
								}}
							>
								Register Your Interest
							</button>
						) : (
							<Tippy
								content={`Please remove other selected tickets to register your interest for this ticket`}
								placement="top"
								arrow={true}
								className="custom-tippy"
							>
								<span>
									<button
										className="ticket-button"
										onClick={() => {
											addTicket(ticket.totalSelected, true);
										}}
										disabled={true}
									>
										Register Your Interest
									</button>
								</span>
							</Tippy>
						)}
					</>
				);
			case !ticket.sold_out && ticket.remaining_count <= 0 && ticket.waitlist_enabled:
				return (
					<>
						{_.isEmpty(props.selectedTickets) ? (
							<button
								className="ticket-button"
								onClick={() => {
									addTicket(ticket.totalSelected, true);
								}}
							>
								Join Waitlist
							</button>
						) : (
							<Tippy
								content={`Please remove other selected tickets to join waitlist`}
								placement="top"
								arrow={true}
								className="custom-tippy"
							>
								<span>
									<button
										className="ticket-button"
										onClick={() => {
											addTicket(ticket.totalSelected, true);
										}}
										disabled={true}
									>
										Join Waitlist
									</button>
								</span>
							</Tippy>
						)}
					</>
				);
			case ticket.sold_out && ticket.waitlist_enabled:
				return (
					<>
						{_.isEmpty(props.selectedTickets) ? (
							<button
								className="ticket-button"
								onClick={() => {
									addTicket(ticket.totalSelected, true);
								}}
							>
								Join Waitlist
							</button>
						) : (
							<Tippy
								content={`Please remove other selected tickets to join waitlist`}
								placement="top"
								arrow={true}
								className="custom-tippy"
							>
								<span>
									<button
										className="ticket-button"
										onClick={() => {
											addTicket(ticket.totalSelected, true);
										}}
										disabled={true}
									>
										Join Waitlist
									</button>
								</span>
							</Tippy>
						)}
					</>
				);
			case ticket.ticket_type === 3:
				return (
					<>
						{!donationAmountEnabled && (
							<button
								className="ticket-button"
								onClick={() => {
									setDonationAmountEnabled(true);
									setDonationEnabled(false);
									donationAmountValidate(Number(ticket.minimum_price));
									if (ticket.minimum_price < 0) {
										setDonationAmount(null);
										addDonationTicket(0);
									}
									addDonationTicket(ticket.minimum_price);
									setDonationAmount(ticket.minimum_price);
								}}
								disabled={!donationEnabled || (otherCurrency && !ticket.is_free)}
							>
								Donate
							</button>
						)}
						{donationAmountEnabled && (
							<div className="donation-amount-wrapper">
								<div className="donation-amount-box">
									<span className="currency">
										<GetCurrency
											fontSize="20px"
											currency={ticket.currency_name}
										/>
									</span>
									<input
										type="number"
										onWheel={e => e.target.blur()}
										className={`donation-amount ${errors.donationAmount ? "border-danger" : ""
											}`}
										value={donationAmount}
										disabled={
											ticket.ticket_type !== 3 || !donationAmountEnabled
										}
										onChange={e => {
											donationAmountValidate(Number(e.target.value));
											e.target.value = Number(e.target.value);
											if (e.target.value < 0) {
												setDonationAmount(null);
												addDonationTicket(0);
											}
											addDonationTicket(e.target.value);
											setDonationAmount(e.target.value);
										}}
										min={ticket.minimum_price}
									/>
									<div className="d-flex align-items-center">
										<i
											className="fas fa-times pointer clear-donation"
											onClick={() => {
												const selectedTicket = ticket;
												selectedTicket.ticket_price = 0;
												selectedTicket.totalSelected = 0;
												selectedTicket.donation_price = 0;
												setTicket(selectedTicket);
												props.removeTicket(selectedTicket);

												setDonationAmount(null);
												setDonationAmountEnabled(false);
												setDonationEnabled(true);
											}}
										/>
									</div>
								</div>
								<p className="text-danger" style={{ fontSize: "12px" }}>
									{errors.donationAmount || ""}
								</p>
							</div>
						)}
					</>
				);
			case ticket.totalSelected === 0 && ticketStartDate < ticketEndDate && !ticket.sold_out:
				return (
					<>
						{disableAddButton(ticket, otherCurrency) &&
							!_.isEmpty(props.selectedTickets) ? (
							<Tippy
								content={disabledAddButtonContent(ticket)}
								placement="top"
								arrow={true}
								className="custom-tippy"
							>
								<span>
									<button
										className="ticket-button"
										onClick={() => addTicket(ticket.totalSelected)}
										disabled={true}
									>
										Add
									</button>
								</span>
							</Tippy>
						) : (
							<button
								className="ticket-button"
								onClick={() => addTicket(ticket.totalSelected)}
							>
								Add
							</button>
						)}
					</>
				);
			case ticket.totalSelected > 0:
				return (
					<div className="d-flex align-items-center justify-content-between counter-btn-container">
						<button
							className="counter-btn"
							onClick={() => removeTicket(ticket.totalSelected)}
						>
							<i className="fa-solid fa-minus"></i>
						</button>
						<p className="tickets-selected m-0">{ticket.totalSelected}</p>
						<Tippy
							content={disablePlusButtonContent()}
							placement="top"
							arrow={true}
							disabled={!disableButton(ticket)}
							className="custom-tippy"
						>
							<span>
								<button
									className={`counter-btn ${disableButton(ticket) ? "counter-btn-disabled" : ""
										}`}
									onClick={() => addTicket(ticket.totalSelected)}
									disabled={disableButton(ticket)}
								>
									<i className="fa-solid fa-plus"></i>
								</button>
							</span>
						</Tippy>
					</div>
				);
			default:
				break;
		}
	};

	const getTicketStatus = ticket => {
		var todaysTime = moment().valueOf();
		// convert utc to local
		const ticketStartTime = moment.utc(`${ticket.start_timestamp}`).local().valueOf();
		const ticketEndTime = moment.utc(`${ticket.end_timestamp}`).local().valueOf();
		const eventEndTime = moment
			.utc(`${eventDetails.end_date} ${eventDetails.end_time}`)
			.local()
			.valueOf();

		switch (true) {
			case todaysTime > ticketEndTime || todaysTime > eventEndTime:
				return "Expired";
			case todaysTime > ticketEndTime:
				return "Sold Out";
			case (ticket.sold_out && !ticket.waitlist_enabled) ||
				(ticket.remaining_count <= 0 && !ticket.waitlist_enabled):
				return "Sold Out";
			case ticketStartTime > todaysTime:
				return "Coming Soon";
			case ticket.approval_enabled:
				return "Approval";
			case !ticket.sold_out && ticket.remaining_count <= 0 && ticket.waitlist_enabled:
				return "Waitlist";
			case ticket.sold_out && ticket.waitlist_enabled:
				return "Waitlist";
			case ticketStartTime < ticketEndTime && !ticket.sold_out:
				return "Register";
			default:
				break;
		}
	};

	const findMaxDiscount = (ticket, groupDiscount) => {
		const { ticket_id, totalSelected, ticket_price } = ticket;

		let maxDiscount = 0;
		let maxDiscountType = 0; // 1 for flat, 2 for percentage
		let discountVal = 0;

		groupDiscount.forEach(discount => {
			const {
				attached_ticket_ids,
				minimum_tickets,
				maximum_tickets,
				coupon_type,
				discount: discountValue
			} = discount;

			// Check if the ticket ID is attached to the discount and if the totalSelected is within the min and max ticket range
			if (
				attached_ticket_ids.includes(ticket_id) &&
				((totalSelected >= minimum_tickets && totalSelected <= maximum_tickets) ||
					(totalSelected === 0 && minimum_tickets === 0))
			) {
				let calculatedDiscount = 0;

				// Calculate the discount based on coupon type
				if (coupon_type === 2) {
					calculatedDiscount = ticket_price - ((discountValue / 100) * ticket_price);
				} else if (coupon_type === 1) {
					calculatedDiscount = discountValue;
				}

				discountVal = discountValue

				// Check if the calculated discount is greater than the current maxDiscount
				if (calculatedDiscount > maxDiscount) {
					maxDiscount = calculatedDiscount;
					maxDiscountType = coupon_type;
				}
			}
		});

		// Calculate the total discount and final price
		let totalDiscount = 0;

		if (maxDiscountType === 2) {
			totalDiscount = (ticket_price - maxDiscount);
		} else if (maxDiscountType === 1) {
			totalDiscount = maxDiscount;
		}

		return { totalDiscount, maxDiscountType, discountVal };
	};

	const showPrice = ticket => {
		const ticketPrice = ticket.ticket_price;
		let { totalDiscount = 0, maxDiscountType = 0, discountVal = 0 } = {};
		const hasMinimumTicketsZero =
			ticket.automatic_discount_rules?.some(rule => rule.minimum_tickets === 0);
		let index;
		if (hasMinimumTicketsZero) {
			index = ticket.automatic_discount_rules.findIndex(rule => rule.minimum_tickets === 0);
		}

		const isGroupDiscount = groupDiscounts?.some(each => each?.attached_ticket_ids?.includes(ticket?.ticket_id))
		if (isGroupDiscount) {
			({ totalDiscount, maxDiscountType, discountVal } = findMaxDiscount(ticket, groupDiscounts));
		}

		if (ticket.discount) {
			return (
				<>
					<span className="ms-1 d-inline-block">
						{ticket.final_price === 0 ? (
							`${isMatchedEvent ? "PAID" : "FREE"}`
						) : (
							<CurrencyWithAmount
								fontSize={"20px"}
								currencyName={ticket.currency_name}
								number={ticket.final_price}
							/>
						)}
					</span>
					<span className="ms-1 currency_number_strikeThrough strike">
						<CurrencyWithAmount
							fontSize={"14px"}
							currencyName={ticket.currency_name}
							number={ticketPrice}
						/>
					</span>
				</>
			);
		} else if (
			hasMinimumTicketsZero &&
			ticket.automatic_discount_rules[index].discount_type == 1
		) {
			return (
				<>
					<span className="ms-1 d-inline-block">
						<CurrencyWithAmount
							fontSize={"20px"}
							currencyName={ticket.currency_name}
							number={Math.round(
								ticketPrice - ticket.automatic_discount_rules[index].discount_value
							)}
						/>
					</span>
					<span className="ms-1 currency_number_strikeThrough strike">
						<CurrencyWithAmount
							fontSize={"14px"}
							currencyName={ticket.currency_name}
							number={ticketPrice}
						/>
					</span>
				</>
			);
		} else if (
			hasMinimumTicketsZero &&
			ticket.automatic_discount_rules[index].discount_type == 2
		) {
			return (
				<>
					<span className="ms-1 d-inline-block">
						<CurrencyWithAmount
							fontSize={"20px"}
							currencyName={ticket.currency_name}
							number={Math.round(
								ticketPrice -
								ticketPrice *
								(ticket.automatic_discount_rules[index].discount_value /
									100)
							)}
						/>
					</span>
					<span className="ms-1 currency_number_strikeThrough strike">
						<CurrencyWithAmount
							fontSize={"14px"}
							currencyName={ticket.currency_name}
							number={ticketPrice}
						/>
					</span>
				</>
			);
		} else if (
			isGroupDiscount && totalDiscount
		) {
			return (
				<>
					<span className="ms-1 d-inline-block">
						<CurrencyWithAmount
							fontSize={"20px"}
							currencyName={ticket.currency_name}
							number={
								ticketPrice - totalDiscount
							}
						/>
					</span>
					<span className="ms-1 currency_number_strikeThrough strike">
						<CurrencyWithAmount
							fontSize={"14px"}
							currencyName={ticket.currency_name}
							number={ticketPrice}
						/>
					</span>
					{ticketPrice > totalDiscount ?
						(<div className="save_group_discount_text">	<div className="mb-2 mb-lg-0 save_group_discount_tag d-flex align-items-center justify-content-center">
							{
								maxDiscountType === 2 ?
									<span>Save{" "}
										<span>
											<CurrencyWithAmount
												fontSize={"12px"}
												currencyName={ticket.currency_name}
												number={totalDiscount}
											/>{` (${discountVal}%)`}
										</span>
									</span>
									:
									<span>Save{" "}
										<span>
											<CurrencyWithAmount
												fontSize={"12px"}
												currencyName={ticket.currency_name}
												// TODO: need to confirm this, also in event page
												//number={Math.min(totalDiscount, ticket.ticket_price * ticket.totalSelected)}
												number={totalDiscount}
											/>
										</span>
									</span>
							}
						</div>
						</div>) : null}
				</>
			);
		} else {
			return (
				<span className="ms-1 d-inline-block">
					<CurrencyWithAmount
						fontSize={"20px"}
						currencyName={ticket.currency_name}
						number={ticketPrice}
					/>
				</span>
			);
		}
	};

	const getRemainingText = count => {
		switch (true) {
			case count === 3:
				return "Only 3 tickets left!";

			case count === 2:
				return "Only 2 tickets left!";

			case count === 1:
				return "Only 1 ticket left!";

			case count <= 10:
				return "Only few tickets left!";

			case count <= 0:
				return null;

			default:
				return null;
		}
	};

	function toggleDiscountContent() {
		setShowDiscountContent(!showDiscountContent);
	}

	const isDiscountApplicable = !ticket.is_free &&
		!ticket.sold_out &&
		moment().valueOf() < moment.utc(`${ticket.end_timestamp}`).local().valueOf() &&
		hasGroupDiscountOffer(groupDiscounts, ticket.ticket_id) &&
		groupDiscounts?.length > 0;

	return (
		<div className="ticket-container w-100 p-3 h-100 d-flex flex-column justify-content-between">
			<div>
				<div className=" d-flex justify-content-between">
					<div className="name-div">
						<h6 className="m-0">{ticket.ticket_name}</h6>
					</div>
					<div
						className="off-ticket-div"
						style={{ marginTop: "-17px", marginRight: "-20px" }}
					>
						{isDiscountApplicable &&
							(smallerScreen ? (
								<>
									<img
										src="/img/off-img.svg"
										alt="off"
										onClick={toggleDiscountContent}
										className="pointer"
									/>
									<DiscountTicketContent
										showModal={showDiscountContent}
										onCloseModal={toggleDiscountContent}
									>
										<div>
											{groupDiscounts
												.filter(item =>
													discount_conditon.includes(item.coupon_type)
												)
												.map((item, index) => (
													<div key={index}>
														{discountContentByCondition(item, ticket)}
													</div>
												))}
										</div>
									</DiscountTicketContent>
								</>
							) : (
								<Tippy
									content={
										<div>
											{groupDiscounts
												.filter(item =>
													discount_conditon.includes(item.coupon_type)
												)
												.map((item, index) => (
													<div key={index}>
														{discountContentByCondition(item, ticket, ticket.ticket_price)}
													</div>
												))}
										</div>
									}
								>
									<img src="/img/off-img.svg" alt="off" className="pointer" />
								</Tippy>
							))}
					</div>
				</div>
				{(isWidget && query.desc == "true" && ticket.description) ||
					(!isWidget && ticket.description) ? (
					<div className="ticket-sub-text m-0 mt-2">
						<ReadMore
							maxChar={"auto"}
							content={ticket.description}
							styles={{
								margin: "0px"
							}}
						/>
					</div>
				) : (
					""
				)}

				{ticket.venue && ticket.latitude && ticket.longitude && showVenue ? (
					<a
						href={`https://www.google.com/maps/search/?api=1&query=${ticket.venue.replace(
							/ /g,
							"+"
						)}`}
						target="_blank"
						className="text-start d-flex mb-1 mt-2"
						style={{
							fontSize: "13px",
							fontFamily: "Prompt",
							textDecoration: "none"
						}}
					>
						<i
							className="fa-solid fa-location-dot mt-1 me-1"
							style={{ fontSize: "14px", cursor: "pointer" }}
						></i>
						{ticket.venue}
					</a>
				) : ticket.venue && showVenue ? (
					<p
						className="mt-2 text-start d-flex mt-1 mb-2"
						style={{ fontSize: "13px", fontFamily: "Prompt" }}
					>
						<i
							className="fa-solid fa-location-dot mt-1 me-1"
							style={{ fontSize: "14px", cursor: "pointer" }}
						></i>
						{ticket.venue}
					</p>
				) : (
					""
				)}
				{ticket.additional_venue_details_rich_text && showAdditionalVenue && (
					<div
						className="mt-0 text-start mb-0"
						style={{ fontSize: "13px", fontFamily: "Prompt", color: "#666666" }}
						dangerouslySetInnerHTML={{
							__html: addAttributesToLinks(DOMPurify.sanitize(
								ticket.additional_venue_details_rich_text
									?.replaceAll(
										'style="font-family: Hind, sans-serif; margin: 0px;"',
										styles
									)
									.replaceAll('style="font-family: Hind, sans-serif;"', styles)
									.replaceAll(
										'style="margin: 0px; font-family: Hind, sans-serif;"',
										styles,
									),
							)),
						}}
					/>
				)}

				<span className="remaining-count">
					{getTicketStatus(ticket) === "Register" &&
						getRemainingText(ticket.remaining_count)}
				</span>
			</div>
			<div className="d-flex flex-md-column flex-sm-row flex-lg-row flex-sm-row justify-content-between align-items-center mt-4 box-height flex-wrap">
				<p className="ticket-price m-0">
					{ticket.currency_name && ticket.ticket_price !== 0 ? (
						<>{showPrice(ticket)}</>
					) : (
						<>
							{ticket.ticket_type === 3 ? (
								<div className="donation-info">
									<span>
										Min:&nbsp;
										<div className="d-inline-block">
											{ticket.minimum_price ? (
												<CurrencyWithAmount
													fontSize="20px"
													currencyName={ticket.currency_name}
													number={ticket.minimum_price}
												/>
											) : (
												"NA"
											)}
										</div>
									</span>
									{ticket.maximum_price && (
										<>
											&nbsp; - &nbsp;
											<span>
												Max:&nbsp;
												<CurrencyWithAmount
													fontSize="20px"
													currencyName={ticket.currency_name}
													number={ticket.maximum_price}
												/>
											</span>
										</>
									)}
								</div>
							) : (
								`${isMatchedEvent ? "PAID" : restrictedEventId ? "" : "FREE"}`
							)}
						</>
					)}
				</p>
				{buttonType(ticket)}
			</div>
			<style jsx global>{`
				.ticket-container {
					background: ${query.ticketBg ? `#${query.ticketBg}` : "#ffffff"};
					box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
					color: ${query.ticketCl ? `#${query.ticketCl}` : "#212529"};
					border-radius: ${query?.borderRadius || 10}px;
				}
				.ticket-sub-text {
					font-size: 14px;
					color: #${query?.fontColor || "474747"};
					word-wrap: break-word;
				}
				.ticket-price {
					font-weight: 500;
					font-size: 24px;
				}
				.ticket-button {
					border: 1px solid
						${query.btnBg ? `#${query.btnBg}` : isWidget ? "#000000" : "#fb5850"};
					background: transparent;
					color: ${query.btnBg ? `#${query.btnBg}` : isWidget ? "#000000" : "#fb5850"};
					border-radius: 5px;
					height: 42px;
					font-size: 14px;
					padding: 10px 25px;
					transition: 0.2s all;
				}
				.ticket-button:disabled {
					-webkit-appearance: none;
					opacity: 0.6;
				}
				.ticket-button:hover {
					color: ${isWidget ? "#ffffff" : query.btnCl ? `#${query.btnCl}` : "#ffffff"};
					background: ${query.btnBg
					? `#${query.btnBg}`
					: isWidget
						? "#000000"
						: "#fb5850"};
				}
				.sold-out-btn {
					color: ${query.btnCl
					? `#${query.btnCl}`
					: isWidget
						? "#000000"
						: "#ffffff !important"};
					background: ${query.btnBg
					? `#${query.btnBg}`
					: isWidget
						? "transparent"
						: "#fb5850 !important"};
					cursor: not-allowed;
					opacity: 0.7;
				}
				.counter-btn {
					-webkit-appearance: none;
					width: 42px !important;
					height: 42px !important;
					border-radius: 100000000000000px;
					background: transparent;
					color: ${query.btnBg ? `#${query.btnBg}` : isWidget ? "#000000" : "#fb5850"};
					font-size: 18px;
					font-weight: 200;
					border: 2px solid
						${query.btnBg ? `#${query.btnBg}` : isWidget ? "#000000" : "#fb5850"};
					line-height: 1rem;
					transition: 0.2s all;
				}
				.counter-btn-disabled {
					-webkit-appearance: none;
					opacity: 0.6;
				}
				.counter-btn-disabled:hover {
					color: ${query.btnCl
					? `#${query.btnCl}`
					: isWidget
						? "#000000"
						: "#ffffff !important"};
					background: ${query.btnBg
					? `#${query.btnBg}`
					: isWidget
						? "#000000"
						: "#fb5850 !important"};
				}
				.counter-btn-container {
					width: auto;
				}

				.counter-btn:hover {
					color: ${isWidget ? "#ffffff" : query.btnCl ? `#${query.btnCl}` : "#ffffff"};
					background: ${query.btnBg
					? `#${query.btnBg}`
					: isWidget
						? "#000000"
						: "#fb5850"};
				}

				.tickets-selected {
					font-weight: 500;
					font-size: 24px;
					margin: 0 5px !important;
				}

				.strike {
					text-decoration: line-through;
					// text-decoration-thickness: 2px;
					opacity: 0.6;
				}
				.currency_number_strikeThrough {
					font-size: 14px !important;
					line-height: 38px;
					vertical-align: text-top;
				}
				.remaining-count {
					font-family: "Prompt" !important;
					font-style: normal !important;
					font-weight: 400 !important;
					font-size: 12px !important;
					line-height: 18px !important;
					color: #dc3545 !important;
				}
				.donation-info {
					font-size: 20px;
				}
				.donation-amount-wrapper {
					width: 150px;
				}
				.donation-amount-box {
					border-radius: 5px;
					border: 2px solid #fb5850;
					background: #fff;
					display: flex;
					width: 100%;
				}
				.donation-amount-box .currency,
				.donation-amount-box .clear-donation {
					padding: 5px 10px;
					font-size: 20px;
				}
				.donation-amount-box .donation-amount {
					width: 100%;
					border: none;
					box-shadow: none;
					padding: 3px;
					outline: none;
				}
				.donation-amount-box .donation-amount:focus,
				.donation-amount-box .donation-amount:active {
					outline: none;
				}

				.save_group_discount_tag {
					width: fit-content;
					min-height: 22px;
					padding: 0px 9px 0px 9px;
					border-radius: 15px;
					background: #FDB14B;
				}
				
				.save_group_discount_text {
					font-family: Prompt;
					font-size: 12px;
					font-weight: 500;
					line-height: 21px;
					letter-spacing: 1px;
					color: #FFFFFF;
				}

				/* SM screens */
				@media only screen and (min-width: 480px) and (max-width: 768px) {
					.donation-amount-wrapper {
						width: 150px;
					}
				}

				/* XS screens */
				@media only screen and (max-width: 480px) {
					.donation-amount-wrapper {
						width: 150px;
					}
				}

				@supports (-webkit-touch-callout: none) and (max-width: 600px) {
					.counter-btn {
						-webkit-appearance: none;
						width: 42px -webkit-fill-available !important;
						height: 42px !important;
						display: flex !important;
						justify-content: center !important;
						align-items: center !important;
						border-radius: 1000000000000000px !important;
						background: transparent;
						color: ${query.btnBg
					? `#${query.btnBg}`
					: isWidget
						? "#000000"
						: "#fb5850"};
						font-size: 20px;
						font-weight: 500;
						border: 2px solid
							${query.btnBg ? `#${query.btnBg}` : isWidget ? "#000000" : "#fb5850"};
						line-height: 1rem;
						transition: 0.2s all;
					}
					.counter-btn-container {
						display: flex !important;
						justify-content: center !important;
						align-items: center !important;
					}

					.tickets-selected {
						margin-left: 10px !important;
						margin-right: 10px !important;
					}
				}
			`}</style>
		</div>
	);
};

export default Ticket;